/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {useContext, useState, useEffect, useCallback} from 'react';
import {LoadingSpinner} from '@albert-io/atomic';
import {requireSession} from 'lib/decorators/requireSession';
import {useTracking} from 'react-tracking';

import {hasAssignmentDraftAccess} from 'lib/UserAccessUtil';
import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';

import DocumentContentDiscovery from './DocumentContentDiscovery/DocumentContentDiscovery.react';
import AssignmentNavigation from './AssignmentNavigation/AssignmentNavigation.react';
import {ShareAssignmentList} from './AssignmentSharing/AssignmentSharing.react';
import UnlicensedAssignmentBuilderView, {showUnliscensedView} from './UnlicensedView.react';
import './content-discovery-2.scss';

interface ContentDiscoveryProps {
  route: string | {path: string};
}

const ContentDiscoveryDocument = ({route}: ContentDiscoveryProps) => {
  const {currentPage, assignmentResults, resetAssignmentBuilder} =
    useContext(ContentDiscoveryContext);
  const [showAssignmentSharing, setShowAssignmentSharing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {Track} = useTracking({
    feature: 'assignment builder'
  });

  useEffect(() => {
    if (assignmentResults.size > 0) {
      setShowAssignmentSharing(true);
    } else {
      setShowAssignmentSharing(false);
    }
  }, [assignmentResults, setShowAssignmentSharing]);

  const handleResetAssignmentBuilder = useCallback(() => {
    setIsLoading(true);
    resetAssignmentBuilder();
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [setIsLoading, resetAssignmentBuilder]);

  const {isReady, value: isUnlicensedNonStudent} = showUnliscensedView();

  if (!isReady || isLoading) {
    return <LoadingSpinner size={3} />;
  }

  if (isUnlicensedNonStudent) {
    return (
      <section className='assignment-document-container'>
        <div className='assignment-body u-display_flex u-flex-direction_row u-bgc_slate-100'>
          <UnlicensedAssignmentBuilderView />
        </div>
      </section>
    );
  }

  return (
    <section className='assignment-document-container'>
      <Track>
        <AssignmentNavigation
          route={route}
          onReset={handleResetAssignmentBuilder}
          showAssignmentSharing={showAssignmentSharing}
        />
        {!showAssignmentSharing && <DocumentContentDiscovery step={currentPage} />}
        {showAssignmentSharing && (
          <div className='assignment-document-sharing-container'>
            <ShareAssignmentList assignments={assignmentResults} />
          </div>
        )}
      </Track>
    </section>
  );
};

const ContentDiscoveryDocumentWithRequiredSession = requireSession({
  rolePredicate: () => {
    const {isReady, value} = hasAssignmentDraftAccess();
    return isReady && value;
  }
})(ContentDiscoveryDocument);

export default ContentDiscoveryDocumentWithRequiredSession;
